import React, { useState } from 'react';
import { Pencil, FileUp, Upload, Eye, Download, Trash2 } from 'lucide-react';
import { Button, Modal, DateInput } from '../../common';
import ConfirmDelete from './ConfirmDelete';
import FileDropArea from './FileDropArea';
import { SearchableSelect } from '../../common/SearchableSelect';
import AddActivityForDocumentButton from './AddActivityForDocumentButton';
import { downloadAttachmentFile } from '../../../services/attachments.service';

export interface DocItem {
  id: number;
  file_name: string;
  file_size?: number;
  attachment_id?: number;
  title?: string;
  notes?: string;
  document_type_id?: number | null;
  document_type_name?: string;
  document_type_code?: string;
}

export interface DocType {
  id: number;
  code?: string;
  name: string;
}

interface DocumentManagerProps {
  documents: DocItem[];
  docTypes?: DocType[];
  requireDocType?: boolean;
  showDateFields?: boolean;
  entityId: number;
  entityPrefix: string;
  // Necesar pentru butonul „Adaugă activitate" de pe fiecare fișier; fără el butonul nu se randează.
  projectId?: number;
  onAdd: (file: File, metadata: Record<string, any>) => Promise<any>;
  onUpdate: (docId: number, data: Record<string, any>) => Promise<any>;
  onDelete: (docId: number) => Promise<void>;
  onRefresh: () => void;
  isAddFormOpen?: boolean;
  onAddFormOpenChange?: (open: boolean) => void;
}

interface DocGroup {
  key: string;
  title: string;
  notes: string;
  docTypeName: string;
  docs: DocItem[];
}

function groupDocuments(docs: DocItem[]): DocGroup[] {
  const groups: DocGroup[] = [];
  for (const doc of docs) {
    const gKey = `${doc.title || ''}|||${doc.notes || ''}`;
    const existing = groups.find(g => g.key === gKey);
    if (existing) {
      existing.docs.push(doc);
    } else {
      groups.push({
        key: gKey,
        title: doc.title || '',
        notes: doc.notes || '',
        docTypeName: doc.document_type_name || '',
        docs: [doc],
      });
    }
  }
  return groups;
}

export default function DocumentManager({
  documents,
  docTypes,
  requireDocType = false,
  showDateFields = false,
  entityId,
  entityPrefix,
  projectId,
  onAdd,
  onUpdate,
  onDelete,
  onRefresh,
  isAddFormOpen,
  onAddFormOpenChange,
}: DocumentManagerProps) {
  // Add document state
  const [internalShowAddForm, setInternalShowAddForm] = useState(false);
  const showAddForm = isAddFormOpen !== undefined ? isAddFormOpen : internalShowAddForm;
  const setShowAddForm = (open: boolean) => {
    if (onAddFormOpenChange) onAddFormOpenChange(open);
    else setInternalShowAddForm(open);
  };
  const [addForm, setAddForm] = useState({ document_type_id: '', title: '', notes: '', start_date: '', end_date: '', nr_registratura: '', data_registratura: '', data_document: '', data_expirare: '' });
  const [addFiles, setAddFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  // Edit document state
  const [editDocId, setEditDocId] = useState<number | null>(null);
  const [editData, setEditData] = useState({ document_type_id: '', title: '', notes: '', start_date: '', end_date: '', nr_registratura: '', data_registratura: '', data_document: '', data_expirare: '' });
  // Tipul documentului la deschiderea formularului - folosit ca sa trimitem document_type_id doar daca s-a schimbat
  const [editInitialDocTypeId, setEditInitialDocTypeId] = useState('');
  const [editFiles, setEditFiles] = useState<File[]>([]);
  const [editGroupIds, setEditGroupIds] = useState<number[]>([]);
  const [savingDoc, setSavingDoc] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ action: () => void; message: string } | null>(null);

  const groups = groupDocuments(documents);

  // --- Add document ---
  const closeAddForm = () => {
    setShowAddForm(false);
    setAddForm({ document_type_id: '', title: '', notes: '', start_date: '', end_date: '', nr_registratura: '', data_registratura: '', data_document: '', data_expirare: '' });
    setAddFiles([]);
  };

  const handleAddDocuments = async () => {
    if (addFiles.length === 0) return;
    if (requireDocType && !addForm.document_type_id) return;
    setUploading(true);
    try {
      for (const file of addFiles) {
        const meta: Record<string, any> = {};
        if (addForm.document_type_id) meta.document_type_id = parseInt(addForm.document_type_id);
        if (addForm.title) meta.title = addForm.title;
        if (addForm.notes) meta.notes = addForm.notes;
        if (showDateFields && addForm.start_date) meta.start_date = addForm.start_date;
        if (showDateFields && addForm.end_date) meta.end_date = addForm.end_date;
        if (addForm.nr_registratura) meta.nr_registratura = addForm.nr_registratura;
        if (addForm.data_registratura) meta.data_registratura = addForm.data_registratura;
        if (addForm.data_document) meta.data_document = addForm.data_document;
        if (addForm.data_expirare) meta.data_expirare = addForm.data_expirare;
        await onAdd(file, meta);
      }
      closeAddForm();
      onRefresh();
    } catch (err) {
      console.error('Add document error:', err);
    } finally {
      setUploading(false);
    }
  };

  // --- Edit document ---
  const startEditDoc = (doc: DocItem) => {
    setEditDocId(doc.id);
    const toDateStr = (v: any) => v ? String(v).substring(0, 10) : '';
    const currentDocTypeId = doc.document_type_id != null ? String(doc.document_type_id) : '';
    setEditData({ document_type_id: currentDocTypeId, title: doc.title || '', notes: doc.notes || '', start_date: '', end_date: '', nr_registratura: (doc as any).nr_registratura || '', data_registratura: toDateStr((doc as any).data_registratura), data_document: toDateStr((doc as any).data_document), data_expirare: toDateStr((doc as any).data_expirare) });
    setEditInitialDocTypeId(currentDocTypeId);
    setEditFiles([]);
    const ids = documents
      .filter(d => (d.title || '') === (doc.title || '') && (d.notes || '') === (doc.notes || ''))
      .map(d => d.id);
    setEditGroupIds(ids.length > 1 ? ids : [doc.id]);
  };

  const cancelEditDoc = () => {
    setEditDocId(null);
    setEditInitialDocTypeId('');
    setEditFiles([]);
    setEditGroupIds([]);
  };

  const handleSaveDoc = async () => {
    if (!editDocId) return;
    setSavingDoc(true);
    try {
      const updatePayload: Record<string, any> = { title: editData.title, notes: editData.notes };
      // Trimitem tipul doar daca a fost efectiv schimbat, ca sa nu suprascriem tipul existent
      if (editData.document_type_id && editData.document_type_id !== editInitialDocTypeId) {
        updatePayload.document_type_id = parseInt(editData.document_type_id);
      }
      if (showDateFields) {
        updatePayload.start_date = editData.start_date;
        updatePayload.end_date = editData.end_date;
      }
      updatePayload.nr_registratura = editData.nr_registratura || undefined;
      updatePayload.data_registratura = editData.data_registratura || undefined;
      updatePayload.data_document = editData.data_document || undefined;
      updatePayload.data_expirare = editData.data_expirare || undefined;
      for (const id of editGroupIds) {
        await onUpdate(id, updatePayload);
      }
      if (editFiles.length > 0) {
        for (const file of editFiles) {
          await onAdd(file, { title: editData.title || undefined, notes: editData.notes || undefined });
        }
      }
      cancelEditDoc();
      onRefresh();
    } catch (err) {
      console.error('Save document error:', err);
    } finally {
      setSavingDoc(false);
    }
  };

  const handleDeleteDoc = async (docId: number) => {
    await onDelete(docId);
    onRefresh();
  };

  const addFormId = `add-doc-${entityPrefix}-${entityId}`;

  return (
    <div>
      {/* Add Document Modal */}
      <Modal
        isOpen={showAddForm}
        onClose={closeAddForm}
        title="Adaugă document"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={closeAddForm}>Anulează</Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleAddDocuments}
              disabled={uploading || addFiles.length === 0 || (requireDocType && !addForm.document_type_id)}
              isLoading={uploading}
              leftIcon={!uploading ? <Upload className="w-4 h-4" /> : undefined}
            >
              {uploading ? 'Se încarcă...' : `Încarcă${addFiles.length > 0 ? ` (${addFiles.length} fișier${addFiles.length > 1 ? 'e' : ''})` : ''}`}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {docTypes && docTypes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tip document{requireDocType ? ' *' : ''}</label>
              <SearchableSelect
                options={docTypes.map(dt => ({ value: dt.id, label: dt.code ? `${dt.code} - ${dt.name}` : dt.name }))}
                value={addForm.document_type_id}
                onChange={v => {
                  const dtId = String(v);
                  const dt = docTypes.find(t => String(t.id) === dtId);
                  setAddForm(d => ({ ...d, document_type_id: dtId, title: dt ? dt.name : d.title }));
                }}
                placeholder="-- Selectează tipul documentului --"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titlu</label>
            <input
              value={addForm.title}
              onChange={e => setAddForm(d => ({ ...d, title: e.target.value }))}
              placeholder="Titlul documentului (opțional)"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observații</label>
            <textarea
              value={addForm.notes}
              onChange={e => setAddForm(d => ({ ...d, notes: e.target.value }))}
              rows={2}
              placeholder="Observații (opțional)"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          {showDateFields && (
            <div className="grid grid-cols-2 gap-4">
              <DateInput label="Data început" value={addForm.start_date} onChange={e => setAddForm(d => ({ ...d, start_date: e.target.value }))} />
              <DateInput label="Data finalizare" value={addForm.end_date} onChange={e => setAddForm(d => ({ ...d, end_date: e.target.value }))} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nr. registratură</label>
              <input value={addForm.nr_registratura} onChange={e => setAddForm(d => ({ ...d, nr_registratura: e.target.value }))} placeholder="Nr. registratură"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
            </div>
            <DateInput label="Data registratură" value={addForm.data_registratura} onChange={e => setAddForm(d => ({ ...d, data_registratura: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DateInput label="Data document" value={addForm.data_document} onChange={e => setAddForm(d => ({ ...d, data_document: e.target.value }))} />
            <DateInput label="Data expirare" value={addForm.data_expirare} onChange={e => setAddForm(d => ({ ...d, data_expirare: e.target.value }))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fișiere *</label>
            <FileDropArea files={addFiles} onFilesChange={setAddFiles} id={addFormId} />
          </div>
        </div>
      </Modal>

      {/* Edit Document Modal */}
      <Modal
        isOpen={editDocId !== null}
        onClose={cancelEditDoc}
        title="Editare document"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={cancelEditDoc}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleSaveDoc} disabled={savingDoc} isLoading={savingDoc}
              leftIcon={!savingDoc ? <Upload className="w-4 h-4" /> : undefined}>
              {savingDoc ? 'Se salvează...' : 'Salvează'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {docTypes && docTypes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tip document{requireDocType ? ' *' : ''}</label>
              <SearchableSelect
                options={docTypes.map(dt => ({ value: dt.id, label: dt.code ? `${dt.code} - ${dt.name}` : dt.name }))}
                value={editData.document_type_id}
                onChange={v => setEditData(d => ({ ...d, document_type_id: String(v) }))}
                placeholder="-- Selectează tipul documentului --"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titlu</label>
            <input
              value={editData.title}
              onChange={e => setEditData(d => ({ ...d, title: e.target.value }))}
              placeholder="Titlul documentului"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observații</label>
            <textarea
              value={editData.notes}
              onChange={e => setEditData(d => ({ ...d, notes: e.target.value }))}
              rows={2}
              placeholder="Observații"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          {showDateFields && (
            <div className="grid grid-cols-2 gap-4">
              <DateInput label="Data început" value={editData.start_date} onChange={e => setEditData(d => ({ ...d, start_date: e.target.value }))} />
              <DateInput label="Data finalizare" value={editData.end_date} onChange={e => setEditData(d => ({ ...d, end_date: e.target.value }))} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nr. registratură</label>
              <input value={editData.nr_registratura} onChange={e => setEditData(d => ({ ...d, nr_registratura: e.target.value }))} placeholder="Nr. registratură"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
            </div>
            <DateInput label="Data registratură" value={editData.data_registratura} onChange={e => setEditData(d => ({ ...d, data_registratura: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DateInput label="Data document" value={editData.data_document} onChange={e => setEditData(d => ({ ...d, data_document: e.target.value }))} />
            <DateInput label="Data expirare" value={editData.data_expirare} onChange={e => setEditData(d => ({ ...d, data_expirare: e.target.value }))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Adaugă fișiere noi</label>
            <FileDropArea files={editFiles} onFilesChange={setEditFiles} id={`edit-doc-${entityPrefix}-${editDocId}`} compact />
          </div>
        </div>
      </Modal>

      {/* Document list (inline) */}
      {documents.length === 0 ? (
        <div className="px-4 py-2 text-xs text-gray-400 italic">Niciun document încărcat</div>
      ) : groups.length > 0 ? (
        <div className="px-4 pb-4 border-t border-gray-200 bg-gray-50">
          <div className="space-y-2 mt-3">
            {groups.map((group) => (
              <div key={group.key} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                <div className="px-4 py-3">
                  {(group.title || group.notes || group.docTypeName) && (
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        {group.docTypeName && !group.title && <div className="text-sm text-gray-700 font-medium">{group.docTypeName}</div>}
                        {group.title && <div className="text-sm text-gray-700 font-medium">{group.title}</div>}
                        {group.notes && <div className="text-xs text-gray-500 mt-0.5">{group.notes}</div>}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => startEditDoc(group.docs[0])}
                          className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-100 transition-colors" title="Editează">
                          <Pencil className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    {group.docs.map((doc) => (
                      <div key={doc.id} className="flex items-center gap-2 text-sm bg-gray-50 rounded-lg px-3 py-2 hover:bg-blue-50 transition-colors">
                        <FileUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="flex-1 truncate text-gray-700 font-medium">{doc.file_name}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0">{doc.file_size ? `${(doc.file_size / 1024).toFixed(0)} KB` : ''}</span>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          {projectId && doc.attachment_id && (
                            <AddActivityForDocumentButton
                              projectId={projectId}
                              attachmentId={doc.attachment_id}
                              fileName={doc.file_name}
                              docTitle={doc.title}
                              variant="icon"
                              size="sm"
                              onCreated={onRefresh}
                            />
                          )}
                          {doc.attachment_id && (
                            <>
                              {doc.file_name?.toLowerCase().endsWith('.pdf') && (
                                <button onClick={() => window.open(`/auth/files/${doc.attachment_id}/${encodeURIComponent(doc.file_name)}?inline=1`, '_blank')}
                                  className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-100 transition-colors" title="Vizualizează">
                                  <Eye className="w-4 h-4" />
                                </button>
                              )}
                              <button onClick={() => downloadAttachmentFile(doc.attachment_id!, doc.file_name)}
                                className="p-1.5 text-gray-400 hover:text-green-600 rounded hover:bg-green-100 transition-colors" title="Descarcă">
                                <Download className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          <button onClick={() => setPendingDelete({ action: () => handleDeleteDoc(doc.id), message: `Ești sigur că vrei să ștergi fișierul "${doc.file_name}"?` })}
                            className="p-1.5 text-gray-400 hover:text-red-500 rounded hover:bg-red-100 transition-colors" title="Șterge fișier">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <ConfirmDelete
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => { if (pendingDelete) pendingDelete.action(); }}
        message={pendingDelete?.message}
      />
    </div>
  );
}
